import { RootState } from '@state/store';
import { UnknownAction } from 'redux';
import { DealFinderErrors, DealFinderState, Destination, Occupancy } from '@model/state/deal-finder-state';
import { ModalType, showModal } from '@state/slices/modal/modalSlice';
import { GeographyResult, hasChildrenWithErrors } from '@model/search';
import { setTabIndexBySearchType } from '@state/slices/search';
import { getDealFinderErrors, isChildValidAgeOnHoliday } from '@state/slices/deal-finder/dealFinderSelectors';
import { performSearch, setToken } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { SearchType } from '@model/iceberg/deal-finder/deal-finder';
import { DealFinderApi } from '@model/iceberg/service/deal-finder/deal-finder-api';
import { DealFinderMapper, PackageDealFinderState } from '@model/iceberg/service/deal-finder/deal-finder-mapper';
import { FILTER_DEFAULTS, MAXIMUM_DESTINATIONS } from '@model/price-calendar';
import { isHotelPath } from '@util/path';
import { getDestinationTags } from '@util/destination';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum DealFinderActions {
  NAME = '@DEAL_FINDER',
  ADD_DESTINATION = '@DEAL_FINDER/addDestination',
  ADD_HOTEL = '@DEAL_FINDER/addHotel',
  REMOVE_DESTINATION = '@DEAL_FINDER/removeDestination',
  CLEAR_DESTINATIONS = '@DEAL_FINDER/clearDestinations',
  SET_AIRPORTS = '@DEAL_FINDER/setAirports',
  SET_DURATION = '@DEAL_FINDER/setDuration',
  SET_DATE = '@DEAL_FINDER/setDate',
  SET_MONTH = '@DEAL_FINDER/setMonth',
  SET_FLEXIBLE_DAYS = '@DEAL_FINDER/setFlexibleDays',
  SET_SEARCH_TYPE = '@DEAL_FINDER/setSearchType',
  DISPLAY_ERRORS = '@DEAL_FINDER/displayErrors',
  SET_ERRORS = '@DEAL_FINDER/setErrors',
  SET_OCCUPANCY = '@DEAL_FINDER/setOccupancy',
  OCCUPANCY_CLEAR = '@DEAL_FINDER/occupancyClear',
  OCCUPANCY_BLUR = '@DEAL_FINDER/occupancyBlur',
  OCCUPANCY_INCREMENT = '@DEAL_FINDER/occupancyIncrement',
  OCCUPANCY_DECREMENT = '@DEAL_FINDER/occupancyDecrement',
  CLEAR_DATES = '@DEAL_FINDER/clearDates',
  VALIDATE_SEARCH = '@DEAL_FINDER/validateSearch',
  HYDRATE_SEARCH = '@DEAL_FINDER/hydrateSearch',
  RECEIVE_CRITERIA_SUCCESS = '@DEAL_FINDER/receiveCriteriaSuccess',
  RECEIVE_CRITERIA_FAILURE = '@DEAL_FINDER/receiveCriteriaFailure',
  RECEIVE_PARTIAL_SEARCH = '@DEAL_FINDER/receivePartialSearch',
  SET_HOTEL_LANDING_SEARCH = '@DEAL_FINDER/setHotelLandingSearch',
  RESET_STATE = '@DEAL_FINDER/resetState'
}

export const INITIAL_DEAL_FINDER_STATE: DealFinderState = {
  searchType: SearchType.MAIN,
  duration: FILTER_DEFAULTS.duration,
  date: '',
  month: '',
  destinations: [],
  airports: [],
  flexibleDays: 0,
  occupancy: [{ adults: 2, children: [] }],
  preferredBoardBasis: '',
  hotelLandingSearch: false,
  destinationSearch: false,
  errors: {
    airports: false,
    dates: false,
    destinations: false,
    occupancy: false
  },
  restoreState: {
    airports: []
  }
};

export type PartialDealFinderState = Pick<
  DealFinderState,
  'destinations' | 'airports' | 'duration' | 'occupancy' | 'month'
> & {
  date?: string;
  preferredBoardBasis?: string;
};

export const hydrateSearch = createAsyncThunk(
  DealFinderActions.HYDRATE_SEARCH,
  async (payload: string, { dispatch, rejectWithValue }) => {
    dispatch(setToken(payload));

    const api: DealFinderApi = new DealFinderApi();
    try {
      const response = await api.criteria(payload);
      if (response) {
        const update: PackageDealFinderState = DealFinderMapper.fromPackageParams(response.data);

        dispatch(setTabIndexBySearchType(update.searchType) as unknown as UnknownAction);
        if (Object.values(SearchType).includes(update.searchType)) {
          dispatch(receiveCriteriaSuccess(update));
        }

        return update;
      } else {
        dispatch(receiveCriteriaFailure());
      }
    } catch (error: any) {
      dispatch(receiveCriteriaFailure());
      return rejectWithValue(error.message);
    }
  }
);

export const validateSearch = createAsyncThunk(
  DealFinderActions.VALIDATE_SEARCH,
  async (payload: DealFinderState, { getState, dispatch }) => {
    const state = getState() as RootState;
    const { airports, destinations, date, month, duration, occupancy, searchType, flexibleDays } = payload;

    // Update the form state with all the new values
    dispatch(
      setPartialSearch({
        airports,
        destinations,
        date,
        month,
        duration,
        occupancy
      })
    );

    // Also update other fields
    if (searchType !== undefined) dispatch(setSearchType(searchType));
    if (flexibleDays !== undefined) dispatch(setFlexibleDays(flexibleDays));

    const hasDate: boolean = !!date || !!month;
    const hasOccupancy: boolean = !hasChildrenWithErrors(occupancy);
    const hotelOnly = searchType === SearchType.HOTEL_ONLY;

    const isFormValid =
      (!!airports.length || hotelOnly) && !!destinations.length && hasDate && !!duration && hasOccupancy;

    if (isFormValid) {
      const isChildDOBsValid = isChildValidAgeOnHoliday(state);
      if (isChildDOBsValid) {
        // Add a small delay to ensure form state updates are reflected in UI
        await new Promise((resolve) => setTimeout(resolve, 50));

        dispatch(
          performSearch({
            ...INITIAL_DEAL_FINDER_STATE,
            airports: hotelOnly ? [] : airports,
            destinations,
            date,
            month,
            duration,
            occupancy,
            searchType,
            flexibleDays
          })
        );
      } else {
        dispatch(showModal({ modalType: ModalType.SEARCH_GUEST_FORM_ERROR }));
        dispatch(setErrors({ ...payload.errors, occupancy: true }));
      }
    }
  }
);

const dealFinderSlice = createSlice({
  name: DealFinderActions.NAME,
  initialState: INITIAL_DEAL_FINDER_STATE,
  reducers: {
    addDestination: (state, action: PayloadAction<GeographyResult>) => {
      const result = action.payload;
      const maxDestinations = state.destinations.length === MAXIMUM_DESTINATIONS;
      const destinationsOnly = state.destinations.filter((destination: Destination) => !isHotelPath(destination.path));

      const destinations = maxDestinations
        ? destinationsOnly
        : destinationsOnly.find(({ path }) => path === result.name.path)
          ? destinationsOnly
          : destinationsOnly.concat({
              ...result.name,
              airports: getDestinationTags(result)
            });

      return {
        ...state,
        destinations,
        destinationSearch: false,
        errors: {
          ...state.errors,
          destinations: false
        }
      };
    },
    addHotel: (state, action: PayloadAction<GeographyResult>) => {
      const result = action.payload;
      return {
        ...state,
        destinations: [{ ...result.name, airports: result._tags }],
        destinationSearch: true,
        errors: {
          ...state.errors,
          destinations: false
        }
      };
    },
    removeDestination: (state, action: PayloadAction<Destination>) => {
      return {
        ...state,
        destinations: state.destinations.filter((destination: Destination) => destination.path !== action.payload.path)
      };
    },
    clearDestinations: (state) => {
      return {
        ...state,
        destinations: []
      };
    },
    setAirports: (state, action: PayloadAction<string[]>) => {
      return {
        ...state,
        airports: action.payload,
        errors: {
          ...state.errors,
          airports: false
        }
      };
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
    },
    setDate: (state, action: PayloadAction<string>) => {
      state.date = action.payload;
      state.month = '';
      state.errors.dates = false;
    },
    setMonth: (state, action: PayloadAction<string>) => {
      state.month = action.payload;
      state.date = '';
      state.errors.dates = false;
    },
    setFlexibleDays: (state, action: PayloadAction<number>) => {
      state.flexibleDays = action.payload;
    },
    setSearchType: (state, action: PayloadAction<SearchType>) => {
      const hotelOnly = action.payload === SearchType.HOTEL_ONLY;
      return {
        ...state,
        searchType: action.payload,
        airports: !hotelOnly ? (state.airports.length ? state.airports : state.restoreState?.airports || []) : [],
        restoreState: {
          airports: hotelOnly && !!state.airports.length ? state.airports : state.restoreState?.airports || []
        }
      };
    },
    clearDates: (state) => {
      state.month = '';
      state.date = '';
      state.flexibleDays = 0;
    },
    setOccupancy: (state, action: PayloadAction<Occupancy[]>) => {
      state.occupancy = action.payload;
    },
    onOccupancyClear: (state) => {
      state.occupancy = INITIAL_DEAL_FINDER_STATE.occupancy;
    },
    onOccupancyBlur: (state) => {
      state.errors.occupancy = hasChildrenWithErrors(state.occupancy);
    },
    occupancyIncrement: (state) => {
      return {
        ...state,
        occupancy: [...state.occupancy, { adults: 1, children: [] }]
      };
    },
    occupancyDecrement: (state) => {
      if (state.occupancy.length === 1) return state;
      return {
        ...state,
        occupancy: state.occupancy.filter((_, index: number) => state.occupancy.length - 1 !== index)
      };
    },
    setErrors: (state, action: PayloadAction<DealFinderErrors>) => {
      state.errors = action.payload;
    },
    displayErrors: (state) => {
      state.errors = getDealFinderErrors(state);
    },
    setPartialSearch: (state, action: PayloadAction<PartialDealFinderState>) => {
      return { ...state, ...action.payload };
    },
    receiveCriteriaSuccess: (state, action: PayloadAction<DealFinderState | PackageDealFinderState>) => {
      return { ...state, ...action.payload };
    },
    receiveCriteriaFailure: (state) => {
      return { ...state };
    },
    setHotelLandingSearch: (state, action: PayloadAction<boolean>) => {
      state.hotelLandingSearch = action.payload;
    },
    resetDealFinder: () => INITIAL_DEAL_FINDER_STATE
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateSearch.fulfilled, (state, action) => {
        return { ...state, ...action.payload };
      })
      .addCase(hydrateSearch.rejected, (state) => {
        // Handle failure scenario
        return state;
      })
      .addCase(validateSearch.fulfilled, (state, action: any) => {
        const { airports, searchType } = action.meta.arg;

        const hotelOnly = searchType === SearchType.HOTEL_ONLY;
        const errors = getDealFinderErrors({ ...state, ...action.payload });
        return {
          ...state,
          ...action.payload,
          airports: hotelOnly ? [] : airports,
          errors
        };
      })
      .addCase(validateSearch.rejected, (state) => {
        return state;
      });
  }
});

export const {
  setDuration,
  setDate,
  setMonth,
  setFlexibleDays,
  addDestination,
  addHotel,
  removeDestination,
  clearDestinations,
  setAirports,
  clearDates,
  setOccupancy,
  occupancyIncrement,
  occupancyDecrement,
  onOccupancyClear,
  onOccupancyBlur,
  setSearchType,
  setErrors,
  displayErrors,
  setPartialSearch,
  receiveCriteriaSuccess,
  receiveCriteriaFailure,
  setHotelLandingSearch,
  resetDealFinder
} = dealFinderSlice.actions;

export const dealFinderActions = {
  validateSearch,
  hydrateSearch
};

export const dealFinderReducer = dealFinderSlice.reducer;
